<template>
  <div class="radial-chart" v-loading="isLoading"><div :id="`activity-radial-${eventType}`"></div></div>
</template>

<script>
import { mapGetters } from 'vuex'

import { chartGlobalLabelDefaults } from '@/constants/chart-global-styles.constants'
import { ACTIVITY_EVENT_REQ } from '@/store/modules/dashboard/kpi-statistics.type'
import {
  ACTIVE_TRAIL_RANGE_REQ,  
  PRIVILEGED_TRAIL_RANGE_REQ
} from '@/store/modules/trails/trailsinfo.type'
import {
  IOC_LIST_RANGE_REQ,
  IOC_DAILY_ACTIVITY_REQ,
  PRIVILEGED_IOC_LIST_RANGE_REQ
} from '@/store/modules/detections/detections.type'

export default {
  name: 'ActivityRadialChart',
  props: {
    eventType: { type: String }
  },
  data () {
    return {
      graphOptions: {
        size: { w: 400, h: 400 },
        style: chartGlobalLabelDefaults.style
      },
      isLoading: false,
      progressionCount: 0,
      detectionCount: 0,
      privilegeCount: 0,
      activitiesCount: 0,
      iocRadialSignals: []
    }
  },
  computed: {
    ...mapGetters(['dailyActvitiyEventReqStatus'])
  },
  methods: {
    gotoProgressionsView () {
      this.$router.push({
        name: 'active-cyber-attacks',
        query: { date_range: 'SINCE_MIDNIGHT' }
      })
    },
    gotoPrivilegedView () {
      this.$router.push({
        name: 'privileged-activities',
        query: { date_range: 'SINCE_MIDNIGHT' }
      })
    },
    gotoDetectionsView () {
      this.$router.push({
        name: 'detections',
        query: { date_range: 'SINCE_MIDNIGHT' }
      })
    },
    gotoActivitiesView () {
      this.$router.push({
        name: 'privileged-detections',
        query: { date_range: 'SINCE_MIDNIGHT' }
      })
    },
    renderChart (activityData, eventType) {
      const vm = this
      const innerCirlce = [-60]
      const bands = [-40, 0, 10, 25, 46]

      const hours = ['12AM', '6AM', '12PM', '6PM']
      const ring = [32, 73]
      const rsum = ring.reduce((a, b) => a + b, 0)
      const rangles = []
      const data = []

      for (let i = 0; i < activityData.length; i++) {
        const n = activityData[i].count
        data.push(n)
      }

      // 24 hour broken into 10 min intervals = (24 * 3600) / 600 = 144
      const full = 144
      const offset = full - data.length

      const svg = vm.$d3
        .select(`#activity-radial-${this.eventType}`)
        .append('svg')
        .style('display', 'block')
        .style('margin-left', 'auto')
        .style('margin-right', 'auto')
        .style('margin-top', this.graphOptions.style.marginTop + 'px' || 'auto')
        .style('width', this.graphOptions.size.w + 'px')
        .style('height', this.graphOptions.size.w + 'px')

      // svg layers
      const outer = svg.append('g')
      const inner = svg.append('g')
      const curve = svg.append('g')
      const info = svg.append('g')
      const clock = svg.append('g')
      const detections = svg.append('g')
      const innerbandrings = svg.append('g')
      const bandrings = svg.append('g')
      const labels = svg.append('g')
      const diagonalLine = svg.append('g')
      const curvetext = svg.append('g')

      let threshold = 1000 * 1000 * 1000
      let threshold2 = 1000 * 1000 * 1000

      const innerRadius = this.graphOptions.size.w * 0.38
      const outerRadius = this.graphOptions.size.w * 0.4
      const ringWidth = 9
      const len = data.length + offset
      const height = this.graphOptions.size.h * 0.08

      const midPercentile = 20
      const topPicks = 5

      let smallFont = '14px'
      let largeFont = '18px'

      const dsorted = [...data]
      dsorted.sort((a, b) => b - a)

      if (data.length > 10) {
        let p = Math.floor((data.length / 100) * midPercentile) - 1
        if (p in dsorted) {
          threshold = dsorted[p]
        }
        if (topPicks - 1 in dsorted) {
          threshold2 = dsorted[topPicks - 1]
        }
      }

      const nmin = data.reduce((min, d) => (d < min ? d : min), data[0])
      const nmax = data.reduce((max, d) => (d > max ? d : max), data[0])
      const heightFactor = height / nmax

      // bar graph
      outer
        .selectAll('line')
        .data(data)
        .enter()
        .append('line')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('opacity', d => {
          if (d > threshold) {
            return 1.0
          }
          return 0.7
        })
        .attr('stroke', d => {
          if (d > threshold) {
            if (d > threshold2) {
              return '#febf04'
            }
            return '#febf04'
          }
          return '#ccc'
        })
        .attr('stroke-width', d => {
          if (d > threshold) {
            if (d > threshold2) {
              return 1.1
            }
            return 1.1
          }
          return 1
        })
        .attr('x1', (d, i) => {
          const point = vm.$d3.pointRadial((i / len) * Math.PI * 2, innerRadius)
          return point[0]
        })
        .attr('y1', (d, i) => {
          const point = vm.$d3.pointRadial((i / len) * Math.PI * 2, innerRadius)
          return point[1]
        })
        .attr('x2', (d, i) => {
          const point = vm.$d3.pointRadial((i / len) * Math.PI * 2, outerRadius + d * heightFactor)
          return point[0]
        })
        .attr('y2', (d, i) => {
          const point = vm.$d3.pointRadial((i / len) * Math.PI * 2, outerRadius + d * heightFactor)
          return point[1]
        })

      let start = 0
      for (let i = 0; i < ring.length; i++) {
        let num = ring[i]
        let endAngle = (num / rsum) * (Math.PI * 2 * (data.length / (data.length + offset))) + start
        rangles.push({
          label: i,
          start: start,
          end: endAngle,
          num: num
        })
        start = endAngle
      }

      const cdata = []
      for (let m = 0; m < data.length; m++) {
        cdata.push([(m / (data.length + offset)) * Math.PI * 2, innerRadius + data[m] * heightFactor - 5])
      }

      // area chart to display trends
      const rgen = vm.$d3.lineRadial().curve(vm.$d3.curveBasis)

      curve
        .append('path')
        .attr('opacity', 0.2)
        .attr('fill', '#57626e')
        .attr('stroke', '#57626e')
        .attr('stroke-width', 1)
        .attr('d', (d, i) => rgen(cdata))
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)

      // const ringgen = vm.$d3
      //   .arc()
      //   .padRadius(1)
      //   .padAngle(Math.PI / 10)
      //   .innerRadius(innerRadius - 2)
      //   .outerRadius(innerRadius - 1);

      // inner ring to depict detection intervals
      // inner
      //   .selectAll("path")
      //   .data(rangles)
      //   .enter()
      //   .append("path")
      //   .attr("id", (d, i) => "ring-" + i)
      //   .attr("d", d =>
      //     ringgen({
      //       startAngle: d.start,
      //       endAngle: d.end
      //     })
      //   )
      //   .attr("fill", (d, i) => {
      //     if (i === 1) {
      //       return "#40a8c4";
      //     }
      //     return "#ffb0b0";
      //   })
      //   .attr("transform", `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`);

      if (innerRadius + innerCirlce[0] < 90) {
        smallFont = '10px'
        largeFont = '12px'
      } else if (innerRadius + innerCirlce[0] > 160) {
        smallFont = '18px'
        largeFont = '24px'
      }

      info
        .append('circle')
        .attr('stroke', 'none')
        .attr('fill', '#fff')
        .attr('r', innerRadius)
        .attr('cx', this.graphOptions.size.w / 2)
        .attr('cy', this.graphOptions.size.w / 2)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', -(innerRadius / 2) + 25)
        .attr('y', -50)
        .attr('text-anchor', 'end')
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', largeFont)
        .attr('fill', '#C8284F')
        .attr('cursor', 'pointer')
        .on('click', this.gotoDetectionsView)
        .text(`${this.detectionCount}`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', -(innerRadius / 2) + 30)
        .attr('y', -50)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', smallFont)
        .attr('fill', '#C8284F')
        .attr('cursor', 'pointer')
        .attr('font-weight', '600')
        .on('click', this.gotoDetectionsView)
        .text(`Detections`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', -(innerRadius / 2) + 25)
        .attr('y', -25)
        .attr('text-anchor', 'end')
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', largeFont)
        .attr('fill', '#C8284F')
        .attr('cursor', 'pointer')
        .on('click', this.gotoProgressionsView)
        .text(`${this.progressionCount}`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', -(innerRadius / 2) + 30)
        .attr('y', -25)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', smallFont)
        .attr('fill', '#C8284F')
        .attr('font-weight', '600')
        .attr('cursor', 'pointer')
        .on('click', this.gotoProgressionsView)
        .text(`Progressions`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2}) rotate(-45)`)
        .attr('x', -innerRadius + 75)
        .attr('y', -10)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', '10px')
        .attr('fill', '#f24a6c')
        .text(`THREATS`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', 25)
        .attr('y', 50)
        .attr('text-anchor', 'end')
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', largeFont)
        .attr('fill', '#179fff')
        .attr('cursor', 'pointer')
        .on('click', this.gotoActivitiesView)
        .text(`${this.activitiesCount}`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', 30)
        .attr('y', 50)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', smallFont)
        .attr('fill', '#179fff')
        .attr('font-weight', '600')
        .attr('cursor', 'pointer')
        .on('click', this.gotoActivitiesView)
        .text(`Activities`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', 25)
        .attr('y', 70)
        .attr('text-anchor', 'end')
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', largeFont)
        .attr('fill', '#179fff')
        .attr('cursor', 'pointer')
        .on('click', this.gotoPrivilegedView)
        .text(`${this.privilegeCount}`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', 30)
        .attr('y', 70)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', smallFont)
        .attr('fill', '#179FFF')
        .attr('font-weight', '600')
        .attr('cursor', 'pointer')
        .on('click', this.gotoPrivilegedView)
        .text(`Trails`)

      labels
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2}) rotate(-45)`)
        .attr('x', innerRadius - 150)
        .attr('y', 20)
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', '10px')
        .attr('fill', '#179FFF')
        .text(`PRIVILEGED`)

      const labelgen = vm.$d3
        .arc()
        .innerRadius(innerRadius + height - 20)
        .outerRadius(outerRadius + height - 30)
        .padRadius(10)
        .padAngle(Math.PI / 10)

      // invisible path to layout date
      curvetext
        .append('path')
        .attr('id', 'arc')
        .attr('opacity', 0)
        .attr(
          'd',
          labelgen({
            startAngle: Math.PI / 32,
            endAngle: Math.PI / 2
          })
        )
        .attr('fill', '#40a8c4')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)

      // date in a curvature
      // curvetext
      //   .append("text")
      //   .attr("font-size", 9)
      //   .attr("x", 3)
      //   .attr("opacity", 0.5)
      //   .append("textPath")
      //   .attr("xlink:href", "#arc")
      //   .text(`${moment().format("MMMM Do YYYY")} - 10m interval`);

      // detections
      detections
        .selectAll('circle')
        .data(vm.iocRadialSignals)
        .enter()
        .append('circle')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('cx', (d, i) => {
          const point = vm.$d3.pointRadial(((1 + d.i) / (data.length + offset)) * Math.PI * 2, innerRadius - 40)
          return point[0]
        })
        .attr('cy', (d, i) => {
          const point = vm.$d3.pointRadial(((1 + d.i) / (data.length + offset)) * Math.PI * 2, innerRadius - 40)
          return point[1]
        })
        .attr('r', 2.5)
        .attr('opacity', d => {
          if (d.threat) {
            return 1.0
          } else {
            return 0.6
          }
        })
        .attr('fill', d => {
          if (d.threat) {
            return '#f24a6c'
          } else {
            return 'none'
          }
        })
        .attr('stroke', d => {
          if (d.threat) {
            return '#f24a6c'
          } else {
            return 'none'
          }
        })
        .attr('stroke-width', 1)

      // hacky way to display hours
      clock
        .selectAll('text')
        .data(hours)
        .enter()
        .append('text')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('x', (d, i) => {
          let extra = 0
          if (i === 1) {
            extra = 25
          } else if (i === 0) {
            extra = 5
          } else if (i === 3) {
            extra = 4
          }
          const point = vm.$d3.pointRadial((i / 4) * Math.PI * 2, innerRadius - 10 - extra)
          return point[0]
        })
        .attr('y', (d, i) => {
          let extra = 0
          if (i === 2) {
            extra = 6
          } else if (i === 0) {
            extra = -7
          }
          const point = vm.$d3.pointRadial((i / 4) * Math.PI * 2, innerRadius - 20 + extra)
          return point[1]
        })
        .attr('font-family', this.graphOptions.style.fontFamily)
        .attr('font-size', 12)
        .text(d => d)

      diagonalLine
        .append('line')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', '4px')
        .attr('x1', `-${this.graphOptions.size.w / 4 - 20}`)
        .attr('y1', this.graphOptions.size.w / 4 - 20)
        .attr('x2', this.graphOptions.size.w / 4 - 20)
        .attr('y2', `-${this.graphOptions.size.w / 4 - 20}`)

      // guidelines
      innerbandrings
        .selectAll('circle')
        .data(innerCirlce)
        .enter()
        .append('circle')
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('fill', '#f3f7ff')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', d => {
          return innerRadius + d
        })

      bandrings
        .selectAll('circle')
        .data(bands)
        .enter()
        .append('circle')
        .attr('opacity', 0.5)
        .attr('transform', `translate(${this.graphOptions.size.w / 2},${this.graphOptions.size.w / 2})`)
        .attr('fill', 'none')
        .attr('stroke', '#666')
        .attr('stroke-width', 0.3)
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', d => {
          return innerRadius + d
        })
    },
    setDimensions (animate = true) {
      this.graphOptions.style.marginTop = -10
      if (
        this.$el.parentElement.clientWidth <= this.$el.parentElement.clientHeight ||
        this.$el.parentElement.clientHeight === 0
      ) {
        this.graphOptions.size.w = `${this.$el.parentElement.clientWidth}`
        if (this.$el.parentElement.clientHeight !== 0) {
          this.graphOptions.style.marginTop =
            (this.$el.parentElement.clientHeight - this.$el.parentElement.clientWidth - 15) / 2
        }
      } else {
        this.graphOptions.size.w = `${this.$el.parentElement.clientHeight}`
      }
      this.graphOptions.size.h = this.graphOptions.size.w
    }
  },
  mounted () {
    this.setDimensions(false)

    this.debounceResize = _.debounce(this.setDimensions, 500)
    // window resize event
    window.addEventListener('resize', this.debounceResize)
  },
  created () {
    if (!_.isEmpty(this.eventType)) {
      this.isLoading = true
      Promise.all([
        this.$store.dispatch(ACTIVITY_EVENT_REQ, this.eventType),
        this.$store.dispatch(ACTIVE_TRAIL_RANGE_REQ),
        this.$store.dispatch(PRIVILEGED_TRAIL_RANGE_REQ),
        this.$store.dispatch(IOC_LIST_RANGE_REQ),
        this.$store.dispatch(PRIVILEGED_IOC_LIST_RANGE_REQ),
        this.$store.dispatch(IOC_DAILY_ACTIVITY_REQ)
      ]).then(
        results => {
          // progressionCount: 0,
          // detectionCount: 0,
          // privilegeCount: 0,
          // activitiesCount: 0
          let eventActivities = results[0]
          this.progressionCount = results[1].length 
          this.privilegeCount = results[2].length
          this.detectionCount = results[3].length
          this.activitiesCount = results[4].length
          for (let idx = 0; idx < results[5].length; idx++) {
            let iocCount = results[5][idx]
            this.iocRadialSignals.push({
              i: Number(idx + 1),
              count: iocCount,
              threat: iocCount > 0
            })
          }
          this.renderChart(eventActivities, this.eventType)
          this.isLoading = false
        },
        errors => {
          console.log(errors)
        }
      )
    }
  },
  beforeDestroy () {
    // remove event listeners when exiting view
    let svg = this.$d3.select(`#activity-radial-${this.eventType}`)
    svg.selectAll('*').remove()

    window.removeEventListener('resize', this.debounceResize)
  }
}
</script>

<style>
@import 'assets/styles/_variables.css';
</style>

<style scoped>
.radial-chart {
  height: 100%;
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/dashboard/ActivityRadialChart.vue